import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ShopRes from "types/res/shop/ShopRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import {
  addMedium,
  bulkInsertMedium,
  deleteMedium,
  fetchMedium,
  updateMedium,
} from "redux/actions/medium";
import MediumRes from "types/res/medium/MediumRes";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateMediumReq from "../../types/req/medium/CreateMediumReq";
import UpdateMediumReq from "../../types/req/medium/UpdateMediumReq";
import { CsvFieldType } from "utils/CsvUtils";
import {
  Box,
  Button,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";
import CsvImport from "components/CsvImport";
import Input from "@material-ui/core/Input";
import Checkbox from "@material-ui/core/Checkbox";

const Medium: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const shops = useSelector((state) => state.shop);
  const media = useSelector((state) => state.medium);
  const [openCsvImport, setOpenCsvImport] = useState(false);
  const [selectShops, setSelectShops] = useState<string[]>([]);
  const [filterMedia, setFilterMedia] = useState<MediumRes[]>([]);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchMedium(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    setSelectShops([...shops.map((shop) => String(shop.shopId)), "すべて"]);
  }, [shops]);

  useEffect(() => {
    const filter = media.filter((medium) => {
      const shopCondition = selectShops.some((shopId) =>
        medium.shops.map((shop) => shop.shopId).includes(Number(shopId))
      );
      return shopCondition;
    });
    setFilterMedia(filter);
  }, [media, selectShops]);

  const headers: { key: keyof MediumRes; label: string }[] = [
    { key: "name", label: t("medium.headers.name") },
    { key: "totalFee", label: t("medium.headers.totalFee") },
    { key: "shopFee", label: t("medium.headers.shopFee") },
    { key: "castFee", label: t("medium.headers.castFee") },
    { key: "monthlyCost", label: t("medium.headers.monthlyCost") },
    { key: "sort", label: t("medium.headers.sort") },
    { key: "isGuestShow", label: t("medium.headers.isGuestShow") },
    { key: "shops", label: t("medium.headers.shops") },
  ];

  const forms = [
    {
      label: t("medium.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("medium.forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
    {
      label: t("medium.forms.shopFee"),
      key: "shopFee",
      type: FormType.Number,
    },
    {
      label: t("medium.forms.castFee"),
      key: "castFee",
      type: FormType.Number,
    },
    {
      label: t("medium.forms.monthlyCost"),
      key: "monthlyCost",
      type: FormType.Number,
    },
    {
      label: t("medium.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("medium.forms.isGuestShow"),
      key: "isGuestShow",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("medium.options.hidden") },
        { id: "1", name: t("medium.options.shown") },
      ],
    },
    {
      label: t("medium.forms.shops"),
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: s.shopId, name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
  ];

  const csvHeader = [
    {
      label: "媒体名",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "割引額",
      key: "totalFee",
      type: CsvFieldType.Number,
    },
    {
      label: "店舗料金",
      key: "shopFee",
      type: CsvFieldType.Number,
    },
    {
      label: "キャスト料金",
      key: "castFee",
      type: CsvFieldType.Number,
    },
    {
      label: "月額費用",
      key: "monthlyCost",
      type: CsvFieldType.Number,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
    {
      label: "顧客モバイル表示",
      key: "isGuestShow",
      type: CsvFieldType.Boolean,
    },
    {
      label: "店舗",
      key: "shops",
      type: CsvFieldType.Array,
      relation: shops,
      relationId: "shopId",
      relationKey: "name",
    },
  ];

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        marginTop={1}
        marginRight={2}
      >
        <Box display="flex" alignItems="center">
          <Box>
            <InputLabel id="select-multiple-shop">{t("medium.filter.shopLabel")}</InputLabel>
            <Select
              multiple
              value={selectShops}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setSelectShops((prev: string[]) => {
                  if (
                    prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [];
                  } else if (
                    !prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") !== -1
                  ) {
                    return [
                      ...shops.map((shop) => String(shop.shopId)),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length === shops.length &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [
                      ...shops.map((shop) => String(shop.shopId)),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length <= shops.length
                  ) {
                    return (event.target.value as string[]).filter(
                      (name) => name !== "すべて"
                    );
                  } else {
                    return event.target.value as string[];
                  }
                });
              }}
              input={<Input id="select-multiple-shop" />}
              style={{ width: "200px", marginRight: "10px" }}
              renderValue={(selected) => {
                if ((selected as string[]).includes("すべて")) {
                  return "すべて";
                } else {
                  return shops
                    .filter((shop) =>
                      (selected as string[]).includes(String(shop.shopId))
                    )
                    .map((shop) => shop.name)
                    .join(", ");
                }
              }}
            >
              <MenuItem key={"すべて"} value={"すべて"}>
                <Checkbox
                  name="all"
                  checked={selectShops.indexOf("すべて") > -1}
                />
                <ListItemText primary={"すべて"} />
              </MenuItem>
              {shops.map((shop) => (
                <MenuItem key={shop.shopId} value={String(shop.shopId)}>
                  <Checkbox
                    checked={selectShops.indexOf(String(shop.shopId)) !== -1}
                  />
                  <ListItemText primary={shop.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenCsvImport((prev) => !prev)}
              style={{ marginLeft: "30px" }}
            >
              {t("medium.csvImport")}
            </Button>
          </Box>
        </Box>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/medium.csv"}
          templateName={"medium_template.csv"}
          csvHeader={csvHeader}
          addType={CreateMediumReq}
          addFunc={(formData) => bulkInsertMedium(companyId, formData)}
        />
      )}
      <MediumTable
        title={t("medium.title")}
        formId="mediumForm"
        forms={forms}
        rows={headers}
        data={filterMedia}
        addType={CreateMediumReq}
        updateType={UpdateMediumReq}
        addFunc={(formData) => addMedium(companyId, formData)}
        updateFunc={(formData) => updateMedium(companyId, formData)}
        deleteFunc={(item) =>
          deleteMedium(companyId, {
            mediumId: item.mediumId,
          })
        }
        values={[
          (s) => s.name,
          (s) => s.totalFee,
          (s) => s.shopFee,
          (s) => s.castFee,
          (s) => s.monthlyCost,
          (s) => s.sort || t("medium.values.unset"),
          (s) => (s.isGuestShow ? t("medium.values.shown") : t("medium.values.hidden")),
          (s) =>
            s.shops.map((shopRes: ShopRes) => shopRes?.name).join(",") ||
            t("medium.values.notAssigned"),
        ]}
      />
    </>
  );
};

const MediumTable = styled<
  React.FC<CommonTableProps<MediumRes, CreateMediumReq, UpdateMediumReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Medium;
